/* Duskhaven product page UX fixes (2026-09-27). Loaded as a module by snippets/dh-pdp-assets.liquid.
   1. <dh-ux-pairs>: loads the complementary "Pairs well with" row when the product has no
      custom.pairs_with metafield (sections/dh-ux-pdp-pairs.liquid), and the one-tap Add button,
      which uses the same cart add + cart:lines-update event as Horizon's product form.
   2. Sticky add-to-cart safety net: keeps Horizon's bar in step with the main buy buttons from
      plain scroll position, so it also appears on phones if its IntersectionObserver misses.
   3. Scale captions: a gallery image whose alt text starts with "Scale:" gets a small caption.
   4. Duskhaven 2.0 (2026-10-03): links to a spec accordion (the light-modes chip -> #dh-spec-light, or a page opened
      with that #hash) open the accordion and bring it into view.
   5. Duskhaven 2.0: in-page jumps first turn off the below-the-fold render deferral (see the note at 5 below). */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

// Horizon declares `Theme` with const in a classic script, so it is a global binding, not window.Theme.
// eslint-disable-next-line no-undef
const routes = () => (typeof Theme !== 'undefined' && Theme.routes) || {};

/* ---------- 1. Pairs well with ---------- */
class DhUxPairs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.state) return;
    const url = this.dataset.url;
    if (!url) return;
    this.dataset.state = 'loading';
    fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html || '', 'text/html');
        const content = doc.querySelector('.dh-ux-pairs__content');
        const row = content && content.querySelector('[data-dh-ux-pair]');
        if (!row || row.dataset.productId === this.dataset.productId) {
          this.dataset.state = 'empty';
          return;
        }
        const heading = content.querySelector('[data-dh-ux-heading]');
        if (heading && this.dataset.heading) heading.textContent = this.dataset.heading;
        const label = content.querySelector('[data-dh-ux-add-label]');
        if (label && this.dataset.buttonLabel) label.textContent = this.dataset.buttonLabel;
        this.replaceChildren(...Array.from(content.childNodes).map((n) => document.importNode(n, true)));
        this.dataset.state = 'ready';
        this.hidden = false;
      })
      .catch(() => {
        this.dataset.state = 'empty';
      });
  }
}
if (!customElements.get('dh-ux-pairs')) customElements.define('dh-ux-pairs', DhUxPairs);

const getCart = () => {
  const items = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (items && typeof items.fetchCartData === 'function') return items.fetchCartData();
  return fetch(`${routes().cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => r.json());
};

const say = (btn, text) => {
  const status = btn.closest('.dh-ux-pairs')?.querySelector('[data-dh-ux-status]');
  if (status) status.textContent = text;
};

async function addPair(btn) {
  if (btn.getAttribute('aria-busy') === 'true') return;
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  const label = btn.querySelector('[data-dh-ux-add-label]');
  const original = btn.dataset.label || (label ? label.textContent : 'Add');
  btn.dataset.label = original;
  btn.setAttribute('aria-busy', 'true');
  say(btn, '');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const cfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(routes().cart_add_url || '/cart/add.js', {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    }).then((r) => r.json());

    const cart = await getCart();
    const base = {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'dh-ux-pairs',
        sourceId: 'dh-ux-pairs',
        itemCount: 1,
        productId: btn.dataset.productId,
      },
    };

    if (response.status) {
      btn.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      deferred.resolve({ ...base, detail: { ...base.detail, didError: true } });
      say(btn, response.description || response.message || 'Sorry, that could not be added.');
      return;
    }

    deferred.resolve({ ...base, detail: { ...base.detail, sections: response.sections, didError: false } });
    btn.classList.add('is-added');
    if (label) label.textContent = 'Added';
    say(btn, 'Added to your cart.');
    setTimeout(() => {
      btn.classList.remove('is-added');
      if (label) label.textContent = original;
    }, 2400);
  } catch (error) {
    deferred.reject(error);
    btn.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    say(btn, 'Sorry, that could not be added. Please try again.');
  } finally {
    btn.removeAttribute('aria-busy');
  }
}

if (!window.__dhUxPdp) {
  window.__dhUxPdp = true;

  document.addEventListener('click', (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const btn = target && target.closest('[data-dh-ux-add]');
    if (!btn) return;
    event.preventDefault();
    addPair(/** @type {HTMLButtonElement} */ (btn));
  });

  /* ---------- 2. Sticky add-to-cart safety net ---------- */
  const initSticky = () => {
    const sticky = /** @type {HTMLElement | null} */ (document.querySelector('.product-information sticky-add-to-cart, sticky-add-to-cart'));
    if (!sticky || sticky.dataset.dhUx) return;
    const bar = /** @type {HTMLElement | null} */ (sticky.querySelector('[ref="stickyBar"]'));
    const section = sticky.closest('.shopify-section');
    const form = section && section.querySelector(`product-form-component[data-product-id="${sticky.dataset.productId}"]`);
    const buy = form && form.closest('.buy-buttons-block');
    if (!bar || !buy) return;
    sticky.dataset.dhUx = 'true';
    const footer = document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
    const chatActive = () => Boolean(customElements.get('shopify-chat') && document.querySelector('shopify-chat'));

    let timer = 0;
    const sync = () => {
      timer = 0;
      const r = buy.getBoundingClientRect();
      const past = r.height > 0 && r.bottom < 0;
      const f = footer ? footer.getBoundingClientRect() : null;
      const atFooter = f ? f.top < window.innerHeight && f.bottom > -200 : false;
      const want = past && !atFooter && !chatActive();
      if ((bar.dataset.stuck === 'true') !== want) bar.dataset.stuck = String(want);
    };
    // setTimeout rather than requestAnimationFrame so it also settles in background tabs.
    const schedule = () => {
      if (!timer) timer = window.setTimeout(sync, 60);
    };
    // Capture phase: from 990px Horizon scrolls .page-wrapper instead of the window.
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  };

  /* ---------- 3. "Scale:" captions on gallery images ---------- */
  const initScale = () => {
    document
      .querySelectorAll('.product-information media-gallery slideshow-slide img[alt^="scale:" i]')
      .forEach((img) => {
        const slide = img.closest('slideshow-slide');
        if (!slide || slide.querySelector('.dh-ux-scale')) return;
        const text = (img.getAttribute('alt') || '').replace(/^scale:\s*/i, '').trim();
        if (!text) return;
        const cap = document.createElement('span');
        cap.className = 'dh-ux-scale';
        cap.setAttribute('aria-hidden', 'true');
        cap.textContent = text;
        slide.appendChild(cap);
      });
  };

  /* ---------- 4. Open a spec accordion from a link or the page's #hash ---------- */
  const openSpec = (hash, scroll) => {
    if (!hash || hash.length < 2) return false;
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch {
      target = null;
    }
    if (!(target instanceof HTMLDetailsElement)) return false;
    target.open = true;
    if (scroll) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      target.querySelector('summary')?.focus({ preventScroll: true });
    }
    return true;
  };
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-dh-open-spec]') : null;
    if (!link) return;
    const hash = link.getAttribute('href') || '';
    if (openSpec(hash, true)) {
      event.preventDefault();
      if (history.replaceState) history.replaceState(history.state, '', hash);
    }
  });
  window.addEventListener('hashchange', () => openSpec(location.hash, false));

  /* ---------- 5. In-page jumps land in the right place ----------
     The sections below the buy box use content-visibility: auto (assets/dh-pdp.css), so their height is only an
     estimate until they come on screen. Before a jump to an anchor on this page (the star rating -> #dh-reviews, or
     a #hash in the address) they switch to full rendering, so the browser scrolls to the target's real position. */
  const cvOff = () => document.documentElement.classList.add('dh-cv-off');
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
      if (!link) return;
      try {
        const url = new URL(link.href, location.href);
        if (url.pathname === location.pathname && url.hash.length > 1) cvOff();
      } catch {
        /* not a URL we can read */
      }
    },
    true
  );
  window.addEventListener('hashchange', cvOff);
  if (location.hash.length > 1) cvOff();

  const init = () => {
    initSticky();
    initScale();
    if (location.hash.startsWith('#dh-spec-')) openSpec(location.hash, true);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}
